import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { linkService } from '../services/linkService';
import { Link } from '../types';
import { QRCodeSVG } from 'qrcode.react';

export default function Dashboard() {
  const [links, setLinks] = useState<Link[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingLink, setEditingLink] = useState<Link | null>(null);
  const [formData, setFormData] = useState({ slug: '', destinationUrl: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadingSize, setDownloadingSize] = useState<string | null>(null);
  
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadLinks();
  }, []);

  async function loadLinks() {
    try {
      const fetchedLinks = await linkService.getLinks();
      setLinks(fetchedLinks);
    } catch (error: any) {
      setError('Failed to load links: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    
    if (!formData.slug || !formData.destinationUrl) {
      setError('Please fill in all fields');
      return;
    }

    // Validate slug format
    if (!/^[a-zA-Z0-9-_]+$/.test(formData.slug)) {
      setError('Slug can only contain letters, numbers, hyphens, and underscores');
      return;
    }

    try {
      setError('');
      
      // Check if slug already exists
      const slugExists = await linkService.slugExists(formData.slug);
      if (slugExists) {
        setError('This slug is already taken. Please choose a different one.');
        return;
      }
      
      await linkService.createLink(formData.slug, formData.destinationUrl, currentUser!.uid);
      setSuccess('Link created successfully!');
      setFormData({ slug: '', destinationUrl: '' });
      setShowCreateForm(false);
      loadLinks();
    } catch (error: any) {
      setError('Failed to create link: ' + error.message);
    }
  }

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    
    if (!editingLink || !formData.slug || !formData.destinationUrl) {
      setError('Please fill in all fields');
      return;
    }

    // Validate slug format
    if (!/^[a-zA-Z0-9-_]+$/.test(formData.slug)) {
      setError('Slug can only contain letters, numbers, hyphens, and underscores');
      return;
    }

    try {
      setError('');
      
      // Check if slug already exists (excluding current link)
      if (formData.slug !== editingLink.slug) {
        const slugExists = await linkService.slugExists(formData.slug, editingLink.id);
        if (slugExists) {
          setError('This slug is already taken. Please choose a different one.');
          return;
        }
      }
      
      await linkService.updateLink(editingLink.id, {
        slug: formData.slug,
        destinationUrl: formData.destinationUrl
      });
      setSuccess('Link updated successfully!');
      setEditingLink(null);
      setFormData({ slug: '', destinationUrl: '' });
      loadLinks();
    } catch (error: any) {
      setError('Failed to update link: ' + error.message);
    }
  }

  async function handleDelete(id: string) {
    if (window.confirm('Are you sure you want to delete this link?')) {
      try {
        await linkService.deleteLink(id);
        setSuccess('Link deleted successfully!');
        loadLinks();
      } catch (error: any) {
        setError('Failed to delete link: ' + error.message);
      }
    }
  }

  function startEdit(link: Link) {
    setEditingLink(link);
    setFormData({ slug: link.slug, destinationUrl: link.destinationUrl });
  }

  function cancelEdit() {
    setEditingLink(null);
    setFormData({ slug: '', destinationUrl: '' });
  }

  function getRedirectUrl(slug: string) {
    return `${window.location.origin}/${slug}`;
  }

           const downloadQRCode = useCallback(async (slug: string, qrElement: HTMLDivElement, size: number) => {
           if (!qrElement) return;
           
           const sizeLabel = `${size}x${size}`;
           setDownloading(slug);
           setDownloadingSize(sizeLabel);
           
           try {
             // Create a canvas element with padding
             const padding = Math.floor(size * 0.1); // 10% padding
             const qrSize = size - (padding * 2);
             
             const canvas = document.createElement('canvas');
             const ctx = canvas.getContext('2d');
             if (!ctx) return;
             
             // Set canvas size to the requested size
             canvas.width = size;
             canvas.height = size;
             
             // Fill white background
             ctx.fillStyle = 'white';
             ctx.fillRect(0, 0, size, size);
             
             // Get the SVG element
             const svgElement = qrElement.querySelector('svg');
             if (!svgElement) return;
             
             // Convert SVG to string
             const svgData = new XMLSerializer().serializeToString(svgElement);
             const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
             const url = URL.createObjectURL(svgBlob);
             
             // Create image from SVG
             const img = new Image();
             img.onload = () => {
               // Draw the QR code image onto canvas with padding
               ctx.drawImage(img, padding, padding, qrSize, qrSize);
               
               // Convert canvas to blob and download
               canvas.toBlob((blob) => {
                 if (blob) {
                   const downloadUrl = URL.createObjectURL(blob);
                   const link = document.createElement('a');
                   link.href = downloadUrl;
                   link.download = `qr-${slug}-${sizeLabel}.png`;
                   document.body.appendChild(link);
                   link.click();
                   document.body.removeChild(link);
                   URL.revokeObjectURL(downloadUrl);
                 }
                 URL.revokeObjectURL(url);
                 setDownloading(null);
                 setDownloadingSize(null);
               }, 'image/png');
             };
             img.src = url;
             
           } catch (error) {
             console.error('Failed to download QR code:', error);
             setDownloading(null);
             setDownloadingSize(null);
           }
         }, []);

  async function handleLogout() {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-amber-200 rounded-full"></div>
            <div className="absolute top-0 left-0 w-20 h-20 border-4 border-amber-600 rounded-full animate-spin border-t-transparent"></div>
          </div>
          <p className="mt-6 text-xl text-gray-600 font-medium">Loading your dashboard...</p>
          <p className="mt-2 text-gray-500">Preparing your link management workspace</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-lg border-b border-amber-100/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-.758l1.102-1.101a4 4 0 00-5.656-5.656l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">BF Links Dashboard</h1>
                <p className="text-sm text-gray-600">Manage your short links and QR codes</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Welcome back,</p>
                <p className="text-gray-900 font-medium">{currentUser?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {error && (
          <div className="mb-8 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl shadow-sm">
            <div className="flex items-center space-x-3">
              <svg className="w-6 h-6 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}
        {success && (
          <div className="mb-8 bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-2xl shadow-sm">
            <div className="flex items-center space-x-3">
              <svg className="w-6 h-6 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">{success}</span>
            </div>
          </div>
        )}

        {/* Create New Link Button */}
        <div className="mb-10">
          <button
            onClick={() => setShowCreateForm(true)}
            className="group bg-gradient-to-r from-amber-600 to-orange-600 text-white px-8 py-4 rounded-2xl font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 flex items-center space-x-3"
          >
            <svg className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Create New Link</span>
          </button>
        </div>

        {/* Create/Edit Form */}
        {(showCreateForm || editingLink) && (
          <div className="bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-2xl border border-white/20 mb-10">
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-.758l1.102-1.101a4 4 0 00-5.656-5.656l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                {editingLink ? 'Edit Link' : 'Create New Link'}
              </h2>
            </div>
            
            <form onSubmit={editingLink ? handleUpdate : handleCreate}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Slug
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-4 py-4 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                    placeholder="Enter slug (e.g., 'my-link')"
                    required
                  />
                                           <p className="mt-2 text-xs text-gray-500">
                           This will be your short URL identifier. Only letters, numbers, hyphens, and underscores allowed.
                         </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Destination URL
                  </label>
                  <input
                    type="url"
                    value={formData.destinationUrl}
                    onChange={(e) => setFormData({ ...formData, destinationUrl: e.target.value })}
                    className="w-full px-4 py-4 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 bg-white/50 backdrop-blur-sm"
                    placeholder="https://example.com"
                    required
                  />
                  <p className="mt-2 text-xs text-gray-500">The URL where users will be redirected</p>
                </div>
              </div>
              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-8 py-3 rounded-2xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  {editingLink ? 'Update Link' : 'Create Link'}
                </button>
                <button
                  type="button"
                  onClick={editingLink ? cancelEdit : () => setShowCreateForm(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-8 py-3 rounded-2xl font-semibold transition-all duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Links List */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
          <div className="px-8 py-6 border-b border-amber-100/50 bg-gradient-to-r from-amber-50/50 to-orange-50/50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Your Links</h3>
                <p className="text-gray-600 mt-1">Manage and share your short links</p>
              </div>
                                   <div className="text-right">
                       <div className="flex space-x-6">
                         <div>
                           <p className="text-3xl font-bold text-amber-600">{links.length}</p>
                           <p className="text-sm text-gray-500">Total Links</p>
                         </div>
                         <div>
                           <p className="text-3xl font-bold text-green-600">
                             {links.reduce((total, link) => total + (link.hits || 0), 0)}
                           </p>
                           <p className="text-sm text-gray-500">Total Hits</p>
                         </div>
                       </div>
                     </div>
            </div>
          </div>
          
          <div className="divide-y divide-amber-100/50">
            {links.map((link) => (
              <div key={link.id} data-link-id={link.id} className="p-8 hover:bg-amber-50/30 transition-all duration-300">
                <div className="flex items-start space-x-6">
                  {/* QR Code Section */}
                  <div className="flex-shrink-0">
                    <div className="bg-white rounded-2xl p-4 border border-amber-200 shadow-lg">
                                                   <div className="qr-code-container">
                               <QRCodeSVG 
                                 value={`${getRedirectUrl(link.slug)}?utm_source=qr-code&utm_medium=qr&utm_campaign=link-${link.slug}`}
                                 size={80}
                                 level="M"
                               />
                             </div>
                    </div>
                    
                    {/* Download Buttons */}
                    <div className="mt-3 space-y-2">
                      <p className="text-xs text-gray-500 text-center mb-2">Download PNG</p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        <button
                          onClick={(e) => {
                            const button = e.currentTarget;
                            const linkItem = button.closest('[data-link-id]');
                            const qrElement = linkItem?.querySelector('.qr-code-container') as HTMLDivElement;
                            if (qrElement) {
                              downloadQRCode(link.slug, qrElement, 512);
                            }
                          }}
                          disabled={downloading === link.slug}
                          title="Download QR code (512x512) - Good for web use"
                          className="w-20 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:bg-gray-400 text-white px-2 py-2 rounded-lg text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center space-x-1 group"
                        >
                          {downloading === link.slug && downloadingSize === '512x512' ? (
                            <>
                              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className="text-xs">...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3 group-hover:scale-110 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              <span className="text-xs">512</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            const button = e.currentTarget;
                            const linkItem = button.closest('[data-link-id]');
                            const qrElement = linkItem?.querySelector('.qr-code-container') as HTMLDivElement;
                            if (qrElement) {
                              downloadQRCode(link.slug, qrElement, 1024);
                            }
                          }}
                          disabled={downloading === link.slug}
                          title="Download QR code (1024x1024) - Great for print and high-res displays"
                          className="w-20 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:bg-gray-400 text-white px-2 py-2 rounded-lg text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center space-x-1 group"
                        >
                          {downloading === link.slug && downloadingSize === '1024x1024' ? (
                            <>
                              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className="text-xs">...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3 group-hover:scale-110 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              <span className="text-xs">1024</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            const button = e.currentTarget;
                            const linkItem = button.closest('[data-link-id]');
                            const qrElement = linkItem?.querySelector('.qr-code-container') as HTMLDivElement;
                            if (qrElement) {
                              downloadQRCode(link.slug, qrElement, 2048);
                            }
                          }}
                          disabled={downloading === link.slug}
                          title="Download QR code (2048x2048) - Perfect for large format printing and billboards"
                          className="w-20 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 disabled:bg-gray-400 text-white px-2 py-2 rounded-xl text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center space-x-1 group"
                        >
                          {downloading === link.slug && downloadingSize === '2048x2048' ? (
                            <>
                              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className="text-xs">...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3 group-hover:scale-110 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              <span className="text-xs">2048</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Link Details */}
                  <div className="flex-1 min-w-0">
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-sm font-semibold text-gray-700">Slug:</span>
                          <span className="font-mono bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 px-4 py-2 rounded-xl text-sm font-medium border border-amber-200">
                            {link.slug}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 break-all">
                          {link.destinationUrl}
                        </p>
                      </div>
                      
                                                   <div className="grid grid-cols-2 gap-4">
                               <div className="bg-gray-50 rounded-xl p-3">
                                 <p className="text-xs text-gray-500 mb-1">Created</p>
                                 <p className="text-sm font-medium text-gray-900">{link.createdAt.toLocaleDateString()}</p>
                               </div>
                               <div className="bg-blue-50 rounded-xl p-3">
                                 <p className="text-xs text-blue-500 mb-1">Redirect URL</p>
                                 <p className="text-sm font-medium text-blue-900 font-mono break-all">
                                   {getRedirectUrl(link.slug)}
                                 </p>
                               </div>
                             </div>
                             
                             {/* Hit Statistics */}
                             <div className="grid grid-cols-2 gap-4">
                               <div className="bg-green-50 rounded-xl p-3">
                                 <p className="text-xs text-green-500 mb-1">Total Hits</p>
                                 <p className="text-sm font-medium text-green-900">{link.hits || 0}</p>
                               </div>
                               <div className="bg-purple-50 rounded-xl p-3">
                                 <p className="text-xs text-purple-500 mb-1">Last Hit</p>
                                 <p className="text-sm font-medium text-purple-900">
                                   {link.lastHitAt ? link.lastHitAt.toLocaleDateString() : 'Never'}
                                 </p>
                               </div>
                             </div>
                             
                             {/* Source Breakdown */}
                             <div className="grid grid-cols-2 gap-4">
                               <div className="bg-blue-50 rounded-xl p-3">
                                 <p className="text-xs text-blue-500 mb-1">QR Code Hits</p>
                                 <p className="text-sm font-medium text-blue-900">{link.qrHits || 0}</p>
                               </div>
                               <div className="bg-orange-50 rounded-xl p-3">
                                 <p className="text-xs text-orange-500 mb-1">Direct Hits</p>
                                 <p className="text-sm font-medium text-orange-900">{link.directHits || 0}</p>
                               </div>
                             </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col space-y-3">
                    <button
                      onClick={() => startEdit(link)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md flex items-center space-x-2 group"
                    >
                      <svg className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(link.id)}
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center space-x-2 group"
                    >
                      <svg className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {links.length === 0 && (
            <div className="px-8 py-16 text-center">
              <div className="text-gray-300 mb-6">
                <svg className="mx-auto h-20 w-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-.758l1.102-1.101a4 4 0 00-5.656-5.656l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No links yet</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Create your first short link to get started! It only takes a few seconds to create a memorable URL with an instant QR code.
              </p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-8 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                Create Your First Link
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
